'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';

import { publicApi } from '@shared/config/axios.config';
import { Button, Modal, Radio, Tooltip } from '@shared/ui/components';
import ArrowIcon from '@public/icons/arrow-right.svg';
import Sphere from '@public/images/sphere.svg';
import Nora from '@public/images/nora-long.svg';
import LikeIcon from '@public/icons/like.svg';
import DialogIcon from '@public/icons/dialogs-simple.svg';
import DiscordIcon from '@public/icons/discord.svg';
import HitIcon from '@public/icons/hit.svg';
import MedalIcon from '@public/icons/medal.svg';
import ClockPlusIcon from '@public/icons/clock-plus.svg';
import InviteIcon from '@public/icons/invite.svg';
import AvatarIcon from '@public/icons/avatar-simple.svg';
import LightningIcon from '@public/icons/lightning-simple.svg';
import GiftIcon from '@public/icons/gift.svg';
import FolderIcon from '@public/icons/folder.svg';
import CrosshairIcon from '@public/icons/crosshair.svg';
import CodeIcon from '@public/icons/code-brackets.svg';
import SquaresIcon from '@public/icons/squares.svg';
import PlusIcon from '@public/icons/plus.svg';
import InfoIcon from '@public/icons/info.svg';
import CheckIcon from '@public/icons/check.svg';

import styles from './Plans.module.scss';

export default function ProgramPlans() {
  const [billingType, setBillingType] = useState<'subscription' | 'full'>('subscription');
  const [ids, setIds] = useState<{ id: string; title: string }[]>([]);
  const [subPopupOpen, setSubPopupOpen] = useState<'basic' | 'prof' | 'academia'>();

  useEffect(() => {
    publicApi.get('/public/plans').then(({ data }) => {
      setIds(data.plans);
    });
  }, []);

  return (
    <>
      <Modal
        isOpen={!!subPopupOpen}
        header="Quantum Leap"
        className={styles.modal}
        headerClassName={styles.modal_header}
        bodyClassName={styles.modal_body}
        separateClose
        close={() => setSubPopupOpen(undefined)}
      >
        <div className={styles.modal_table}>
          <div className={styles.modal_table_row}>
            <div className={styles.modal_table_row_title}>
              <div className={styles.modal_table_row_title_title}>Quantum Gates</div>
              <div className={styles.modal_table_row_title_text}>
                Maximum simultaneous quantum gates
              </div>
            </div>

            <div className={styles.modal_table_row_value}>1000</div>
          </div>

          <div className={styles.modal_table_row}>
            <div className={styles.modal_table_row_title}>
              <div className={styles.modal_table_row_title_title}>Quantum Circuits</div>
              <div className={styles.modal_table_row_title_text}>
                Maximum simultaneous quantum circuits (num_reads)
              </div>
            </div>

            <div className={styles.modal_table_row_value}>1024</div>
          </div>

          <div className={styles.modal_table_row}>
            <div className={styles.modal_table_row_title}>
              <div className={styles.modal_table_row_title_title}>Job Duration</div>
              <div className={styles.modal_table_row_title_text}>
                Maximum runtime per compute job
              </div>
            </div>

            <div className={styles.modal_table_row_value}>60 Minutes</div>
          </div>

          <div className={styles.modal_table_row}>
            <div className={styles.modal_table_row_title}>
              <div className={styles.modal_table_row_title_title}>Basic Support</div>
              <div className={styles.modal_table_row_title_text}>
                Access to Dynex ticketing system and community support on Slack
              </div>
            </div>

            <div className={styles.modal_table_row_value}>
              <Image
                src={CheckIcon}
                className={styles.modal_table_row_value_check}
                alt="Check"
                draggable={false}
              />
            </div>
          </div>
        </div>
      </Modal>

      <div className={styles.container}>
        <Image src={Nora} className={styles.nora_1} alt="Nora" draggable={false} />
        <Image src={Nora} className={styles.nora_2} alt="Nora" draggable={false} />

        <div className={styles.header}>
          <div className={styles.header_title}>
            ALL THE KNOWLEDGE YOU NEED
            <br className={styles.br} />
            IN ONE PLAN
          </div>

          <div className={styles.earlyBird}>
            <Image src={LikeIcon} className={styles.earlyBird_icon} alt="Like" draggable={false} />

            <div className={styles.earlyBird_body}>
              <div className={styles.earlyBird_body_title}>Early Bird Promotion 20% off</div>
              <div className={styles.earlyBird_body_text}>*until 30th of November 2025</div>
            </div>
          </div>
        </div>

        <div className={styles.assessmentCard}>
          <Image
            src={Sphere}
            className={styles.assessmentCard_sphere_1}
            alt="Sphere"
            draggable={false}
          />
          <Image
            src={Sphere}
            className={styles.assessmentCard_sphere_2}
            alt="Sphere"
            draggable={false}
          />

          <div className={styles.assessmentCard_header}>
            <div className={styles.assessmentCard_header_title}>
              START WITH YOUR PERSONAL ASSESSMENT
            </div>
            <div className={styles.assessmentCard_header_price}>
              95 <span className={styles.assessmentCard_header_price_strong}>CHF/user *</span>
            </div>
          </div>

          <div className={styles.assessmentCard_body}>
            Your initial assessment is designed to evaluate your current knowledge, technical
            background, and preparedness for quantum learning. It generates a personalized learning
            path and recommends the optimal entry point into the program, presented in a detailed
            post-assessment report.
          </div>

          <div className={styles.assessmentCard_footer}>
            <div className={styles.assessmentCard_discount}>
              Student Discount <span className={styles.assessmentCard_discount_strong}>30</span>%
              <br />
              Freelancer / Start - Up Discount{' '}
              <span className={styles.assessmentCard_discount_strong}>15</span>% **
            </div>

            <Link href={`/plan/${ids.find(({ title }) => /assessment/i.test(title))?.id}`}>
              <Button size="l" variant="filled" className={styles.button}>
                <div>START NOW</div>
                <Image src={ArrowIcon} alt="Arrow" draggable={false} />
              </Button>
            </Link>
          </div>
        </div>

        <div className={styles.plans_title}>Choose your Q:EDU PROGRAM plan</div>

        <Radio
          value={billingType}
          options={[
            { value: 'subscription', label: 'Billed Weekly' },
            { value: 'full', label: 'Billed in Full' },
          ]}
          direction="horizontal"
          className={styles.radio}
          indicatorClassName={styles.radio_indicator}
          indicatorActiveClassName={styles.radio_indicator_active}
          onChange={(value) => setBillingType(value as 'subscription' | 'full')}
        />

        <div id="plans" className={styles.plans_grid}>
          <div className={styles.plans_grid_top}>
            <div id="quantum-basic" className={styles.plan}>
              <div className={styles.plan_header}>
                Personalised Educational Program
                <br />
                Quantum Computing
              </div>

              <div className={styles.plan_title}>
                <div className={styles.plan_title_title}>Quantum Basic</div>
                <div className={styles.plan_title_price}>
                  12 Weeks | {billingType === 'subscription' ? '72,50 CHF/week' : '870 CHF'} *
                </div>
              </div>

              <div className={styles.plan_body}>
                {[
                  {
                    icon: AvatarIcon,
                    content: <>1 User</>,
                  },
                  {
                    icon: LightningIcon,
                    content: (
                      <>
                        3 Day free trial to explore{' '}
                        <Tooltip
                          anchor={
                            <Image
                              src={InfoIcon}
                              className={styles.info}
                              alt="Info"
                              draggable={false}
                            />
                          }
                          className={styles.tooltip}
                        >
                          Limited Insight in the Program plan
                        </Tooltip>
                      </>
                    ),
                  },
                  {
                    icon: GiftIcon,
                    content: <>Incl. free Module Kick Off with Python</>,
                  },
                  {
                    icon: FolderIcon,
                    content: (
                      <>
                        Access to <span className={styles.plan_strong_color}>Module 1</span> &{' '}
                        <span className={styles.plan_strong_color}>2</span>: Password Secured
                      </>
                    ),
                  },
                  {
                    icon: CodeIcon,
                    content: (
                      <>
                        <span className={styles.plan_strong}>Learning Outcome:</span> Master the
                        math and physics that make quantum tick—linear-algebra, qubits,
                        superposition, entanglement—then round it out with a principled look at
                        privacy, cryptography and policy so you can discuss quantum tech with both
                        confidence and conscience.
                      </>
                    ),
                  },
                  {
                    icon: SquaresIcon,
                    content: (
                      <>
                        Access to Training Surrounding and Computational Subscription{' '}
                        <Image
                          src={InfoIcon}
                          className={styles.info}
                          alt="Info"
                          draggable={false}
                          onClick={() => setSubPopupOpen('basic')}
                        />
                      </>
                    ),
                  },
                  {
                    icon: DialogIcon,
                    content: <>24/7 dedicated helpline</>,
                  },
                  {
                    icon: DiscordIcon,
                    content: <>Access to Community Discord</>,
                  },
                  {
                    icon: HitIcon,
                    content: (
                      <>
                        Final Assessments with Your Performance Report:
                        <br />3 Attempts per module to pass assessments successfully during the 12
                        weeks period
                      </>
                    ),
                  },
                  {
                    icon: MedalIcon,
                    content: (
                      <>
                        Your Certificate of Completion:{' '}
                        <span className={styles.plan_strong_color}>Achiever of Quantum Basic</span>
                      </>
                    ),
                  },
                  {
                    icon: InviteIcon,
                    content: (
                      <>
                        Post Successful completion of program plan:
                        <br />
                        Access to Alumni Club
                      </>
                    ),
                  },
                ].map(({ icon, content }, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={index} className={styles.plan_body_item}>
                    <Image src={icon} alt="Icon" draggable={false} />
                    <div>{content}</div>
                  </div>
                ))}
              </div>

              <div className={styles.plan_discount}>
                Student Discount <span className={styles.plan_discount_strong}>30</span>%
                <br />
                Freelancer / Start - Up Discount{' '}
                <span className={styles.plan_discount_strong}>15</span>% **
              </div>

              <Link href={`/plan/${ids.find(({ title }) => /basic/i.test(title))?.id}`}>
                <Button size="l" variant="filled" className={styles.plan_button}>
                  <div>START NOW</div>
                  <Image src={ArrowIcon} alt="Arrow" draggable={false} />
                </Button>
              </Link>
            </div>

            <div id="quantum-professional" className={styles.plan}>
              <div className={styles.plan_header}>
                Personalised Educational Program
                <br />
                Quantum Computing
              </div>

              <div className={styles.plan_title}>
                <div className={styles.plan_title_title}>Quantum Professional</div>
                <div className={styles.plan_title_price}>
                  12 Weeks | {billingType === 'subscription' ? '98,30 CHF/week' : '1180 CHF'} *
                </div>
              </div>

              <div className={styles.plan_body}>
                {[
                  {
                    icon: AvatarIcon,
                    content: <>1 User</>,
                  },
                  {
                    icon: LightningIcon,
                    content: (
                      <>
                        3 Day free trial to explore{' '}
                        <Tooltip
                          anchor={
                            <Image
                              src={InfoIcon}
                              className={styles.info}
                              alt="Info"
                              draggable={false}
                            />
                          }
                          className={styles.tooltip}
                        >
                          Limited Insight in the Program plan
                        </Tooltip>
                      </>
                    ),
                  },
                  {
                    icon: GiftIcon,
                    content: <>Incl. free Module Kick Off with Python</>,
                  },
                  {
                    icon: FolderIcon,
                    content: (
                      <>
                        Access to <span className={styles.plan_strong_color}>Module 1</span>,{' '}
                        <span className={styles.plan_strong_color}>2</span> &{' '}
                        <span className={styles.plan_strong_color}>3</span>: Password Secured
                      </>
                    ),
                  },
                  {
                    icon: CodeIcon,
                    content: (
                      <>
                        <span className={styles.plan_strong}>Learning Outcome:</span> Take the
                        fundamentals further by coding quantum gates and hallmark algorithms in the
                        Dynex SDK, cracking classic “toy” optimisation and search problems that
                        prove you can turn theory into working quantum circuits.
                      </>
                    ),
                  },
                  {
                    icon: SquaresIcon,
                    content: (
                      <>
                        Access to Training Surrounding and Computational Subscription{' '}
                        <Image
                          src={InfoIcon}
                          className={styles.info}
                          alt="Info"
                          draggable={false}
                          onClick={() => setSubPopupOpen('prof')}
                        />
                      </>
                    ),
                  },
                  {
                    icon: DialogIcon,
                    content: <>24/7 dedicated helpline</>,
                  },
                  {
                    icon: DiscordIcon,
                    content: <>Access to Community Discord</>,
                  },
                  {
                    icon: HitIcon,
                    content: (
                      <>
                        Final Assessments with Your Performance Report:
                        <br />3 Attempts per module to pass assessments successfully during the 12
                        weeks period
                      </>
                    ),
                  },
                  {
                    icon: MedalIcon,
                    content: (
                      <>
                        Your Certificate of Completion:{' '}
                        <span className={styles.plan_strong_color}>
                          Achiever of Quantum Professional
                        </span>
                      </>
                    ),
                  },
                  {
                    icon: InviteIcon,
                    content: (
                      <>
                        Post Successful completion of program plan:
                        <br />
                        Access to Alumni Club
                      </>
                    ),
                  },
                  {
                    icon: PlusIcon,
                    content: (
                      <>
                        You are a Quantum Basic Alumni: Single Price Module 3 - 310 CHF *
                        {/* {billingType === 'subscription' ? '25,80 CHF/week' : '310 CHF'} * */}
                      </>
                    ),
                  },
                ].map(({ icon, content }, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={index} className={styles.plan_body_item}>
                    <Image src={icon} alt="Icon" draggable={false} />
                    <div>{content}</div>
                  </div>
                ))}
              </div>

              <div className={styles.plan_discount}>
                Student Discount <span className={styles.plan_discount_strong}>30</span>%
                <br />
                Freelancer / Start - Up Discount{' '}
                <span className={styles.plan_discount_strong}>15</span>% **
              </div>

              <Link href={`/plan/${ids.find(({ title }) => /professional/i.test(title))?.id}`}>
                <Button size="l" variant="filled" className={styles.plan_button}>
                  <div>START NOW</div>
                  <Image src={ArrowIcon} alt="Arrow" draggable={false} />
                </Button>
              </Link>
            </div>

            <div id="quantum-academia" className={styles.plan}>
              <div className={styles.plan_header}>
                Personalised Educational Program
                <br />
                Quantum Computing | Academically Accredited
              </div>

              <div className={styles.plan_title}>
                <div className={styles.plan_title_title}>Quantum Academia</div>
                <div className={styles.plan_title_price}>
                  12 Weeks | {billingType === 'subscription' ? '124,15 CHF/week' : '1490 CHF'} *
                </div>
              </div>

              <div className={styles.plan_body}>
                {[
                  {
                    icon: AvatarIcon,
                    content: <>1 User</>,
                  },
                  {
                    icon: LightningIcon,
                    content: (
                      <>
                        3 Day free trial to explore{' '}
                        <Tooltip
                          anchor={
                            <Image
                              src={InfoIcon}
                              className={styles.info}
                              alt="Info"
                              draggable={false}
                            />
                          }
                          className={styles.tooltip}
                        >
                          Limited Insight in the Program plan
                        </Tooltip>
                      </>
                    ),
                  },
                  {
                    icon: GiftIcon,
                    content: <>Incl. free Module Kick Off with Python</>,
                  },
                  {
                    icon: FolderIcon,
                    content: (
                      <>
                        Access to <span className={styles.plan_strong_color}>Module 1</span>,{' '}
                        <span className={styles.plan_strong_color}>2</span>,{' '}
                        <span className={styles.plan_strong_color}>3</span> &{' '}
                        <span className={styles.plan_strong_color}>4</span>: Password Secured
                      </>
                    ),
                  },
                  {
                    icon: CrosshairIcon,
                    content: <>Access to Module 5: Final Capstone Project</>,
                  },
                  {
                    icon: CodeIcon,
                    content: (
                      <>
                        <span className={styles.plan_strong}>Learning Outcome:</span> Push into
                        today&apos;s research frontiers—quantum annealing, hybrid QC/ML workflows,
                        LLMs on QPUs, topological qubits, post-quantum security—then showcase your
                        expertise in a mentored capstone on the Dynex platform, solving a real-world
                        challenge worthy of publication or R&D portfolios.
                      </>
                    ),
                  },
                  {
                    icon: SquaresIcon,
                    content: (
                      <>
                        Access to Training Surrounding and Computational Subscription{' '}
                        <Image
                          src={InfoIcon}
                          className={styles.info}
                          alt="Info"
                          draggable={false}
                          onClick={() => setSubPopupOpen('academia')}
                        />
                      </>
                    ),
                  },
                  {
                    icon: DialogIcon,
                    content: <>24/7 dedicated helpline</>,
                  },
                  {
                    icon: DiscordIcon,
                    content: <>Access to Community Discord</>,
                  },
                  {
                    icon: HitIcon,
                    content: (
                      <>
                        Final Assessments with Your Performance Report:
                        <br />3 Attempts per module to pass assessments successfully during the 12
                        weeks period
                      </>
                    ),
                  },
                  {
                    icon: MedalIcon,
                    content: (
                      <>
                        Your Certificate of Completion:{' '}
                        <span className={styles.plan_strong_color}>
                          Achiever of Quantum Academia
                        </span>
                      </>
                    ),
                  },
                  {
                    icon: ClockPlusIcon,
                    content: <>Credited hours</>,
                  },
                  {
                    icon: InviteIcon,
                    content: (
                      <>
                        Post Successful completion of program plan:
                        <br />
                        Access to Alumni Club
                      </>
                    ),
                  },
                  {
                    icon: PlusIcon,
                    content: (
                      <>
                        You are a Quantum Basic Alumni: Single Price Module 3 + 4 - 620 CHF *
                        {/* {billingType === 'subscription' ? '51,65 CHF/week' : '620 CHF'} * */}
                      </>
                    ),
                  },
                  {
                    icon: PlusIcon,
                    content: (
                      <>
                        You are a Quantum Professional Alumni: Single Price Module 4 - 310 CHF *
                        {/* {billingType === 'subscription' ? '25,80 CHF/week' : '310 CHF'} * */}
                      </>
                    ),
                  },
                ].map(({ icon, content }, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={index} className={styles.plan_body_item}>
                    <Image src={icon} alt="Icon" draggable={false} />
                    <div>{content}</div>
                  </div>
                ))}
              </div>

              <div className={styles.plan_discount}>
                Student Discount <span className={styles.plan_discount_strong}>30</span>%
                <br />
                Freelancer / Start - Up Discount{' '}
                <span className={styles.plan_discount_strong}>15</span>% **
              </div>

              <Link href={`/plan/${ids.find(({ title }) => /academia/i.test(title))?.id}`}>
                <Button size="l" variant="filled" className={styles.plan_button}>
                  <div>START NOW</div>
                  <Image src={ArrowIcon} alt="Arrow" draggable={false} />
                </Button>
              </Link>
            </div>
          </div>

          <div className={styles.plans_grid_bottom}>
            <div id="enterprise" className={styles.plan}>
              <div className={styles.smallPlan}>
                <div className={styles.smallPlan_body}>
                  <div className={styles.smallPlan_body_subtitle}>
                    Customized modular teaching program
                  </div>
                  <div className={styles.smallPlan_body_title}>Enterprise</div>
                  <div className={styles.smallPlan_body_text}>
                    Price Available upon Consultation
                  </div>
                </div>

                <Link href="/?contact-us=true">
                  <Button size="l" variant="filled" className={styles.smallPlan_button}>
                    <div>Contact us</div>
                    <Image src={ArrowIcon} alt="Arrow" draggable={false} />
                  </Button>
                </Link>
              </div>
            </div>

            <div id="teams" className={styles.plan}>
              <div className={styles.smallPlan}>
                <div className={styles.smallPlan_body}>
                  <div className={styles.smallPlan_body_subtitle}>Contact Sales Team</div>
                  <div className={styles.smallPlan_body_title}>Teams or Multiple Users</div>
                  <div className={styles.smallPlan_body_text}>
                    Price Available upon Consultation
                  </div>
                </div>

                <Link href="/?contact-us=true">
                  <Button size="l" variant="filled" className={styles.smallPlan_button}>
                    <div>Contact us</div>
                    <Image src={ArrowIcon} alt="Arrow" draggable={false} />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.hints}>
          <div>* Product prices may be subject to change without prior notice</div>
          <div>
            ** * By claiming this discount, you confirm that the information you have provided is
            true, accurate, and complete. Any false or misleading declarations may result in
            disqualification from the offer or future services
          </div>
        </div>
      </div>
    </>
  );
}
